import { FC, useMemo } from 'react';

import dayjs from 'dayjs';
import useSWR from 'swr';

import CircularProgress from '@mui/material/CircularProgress';

import { EditNotificationColumn } from './publishedNotificationsTableColumns/EditNotificationColumn';
import { PinnedCheckboxColumn } from './publishedNotificationsTableColumns/PinnedCheckboxColumn';

import css from './PublishedNotifications.module.css';

import { broadcastNotificationService } from 'v2/api/broadcastNotificationService';
import { DataTable } from 'v2/components/dataTable/DataTable';
import { usePagination } from 'v2/hooks/usePagination';
import { TableColumn } from 'v2/types';
import { BroadcastNotification } from 'v2/types/BroadcastNotificationModel';

export const PublishedNotifications: FC = () => {
  const pagination = usePagination();

  const { data, mutate, isLoading } = useSWR(
    ['getBroadcastNotifications', pagination.page, pagination.pageSize],
    broadcastNotificationService.getList,
    { keepPreviousData: true }
  );

  const columns: TableColumn<BroadcastNotification>[] = useMemo(
    () => [
      {
        key: 'title',
        label: 'Title',
        ellipsis: true,
        render: (row) => Object.values(row.data)[0]?.title,
      },
      {
        key: 'description',
        label: 'Description',
        ellipsis: true,
        render: (row) => Object.values(row.data)[0]?.description,
      },
      {
        key: 'categorySubType',
        label: 'Category',
        ellipsis: true,
        render: (row) => row.categorySubType,
      },
      {
        key: 'createdAt',
        label: 'Creation date',
        render: (row) => dayjs(row.createdAt).format('DD.MM.YYYY HH:mm'),
      },
      {
        key: 'isPinned',
        label: 'Pinned',
        align: 'center',
        render: (row) => <PinnedCheckboxColumn item={row} onRevalidate={mutate} />,
      },
      {
        key: 'actions',
        align: 'right',
        render: (row) => <EditNotificationColumn item={row} onRevalidate={mutate} />,
      },
    ],
    [mutate]
  );

  if (isLoading) {
    return (
      <div className={css.loader}>
        <CircularProgress size={32} />
      </div>
    );
  }

  return (
    <div className={css.container}>
      <DataTable
        columns={columns}
        data={data?.result}
        withPagination
        total={data?.total}
        page={pagination.page}
        pageSize={pagination.pageSize}
        onPageChange={pagination.onPageChange}
      />
    </div>
  );
};
